import { Checkbox } from "design-system/atoms/Checkbox"
import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  ConversationExperimentProductionName,
  GetBackofficeConversationExperimentsSchema,
} from "schema"
import { z } from "zod"

type Search = z.infer<typeof GetBackofficeConversationExperimentsSchema>

type Props = {
  search: Search
  experimentNames: string[] | undefined
  onExcludeProductionChange: (excludeProduction: boolean) => void
  onExperimentChange: (experiment: string | undefined) => void
}

export function BackofficeConversationExperimentsFilters({
  search,
  experimentNames,
  onExcludeProductionChange,
  onExperimentChange,
}: Props) {
  const { t } = useTranslation()

  const availableExperiments = useMemo(() => {
    const names = experimentNames ?? []
    if (search.excludeProduction) {
      return names.filter((name) => name !== ConversationExperimentProductionName)
    }
    return names
  }, [experimentNames, search.excludeProduction])

  const selectedExperiment = search.experiment ?? "all"

  return (
    <div className="flex flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id="exclude-production"
          checked={search.excludeProduction}
          onCheckedChange={(checked) => onExcludeProductionChange(checked === true)}
        />
        <Label htmlFor="exclude-production" className="cursor-pointer font-normal">
          {t("backoffice.conversationExperiments.filters.excludeProduction")}
        </Label>
      </div>

      <Select
        value={selectedExperiment}
        onValueChange={(value) => onExperimentChange(value === "all" ? undefined : value)}
      >
        <SelectTrigger className="h-9 ml-auto w-56 max-w-full shrink-0">
          <SelectValue
            placeholder={t("backoffice.conversationExperiments.filters.allExperiments")}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            {t("backoffice.conversationExperiments.filters.allExperiments")}
          </SelectItem>
          {availableExperiments.map((name) => (
            <SelectItem key={name} value={name}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
